import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Button,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import CommentInputForReason from "./CommentInputForReason";
import {
  DCS_CONSUMER_APPLICATION_INCHARGE,
  DCS_CONSUMER_APPLICATION_OPERATOR,
  POWER_CONSUMER_APPLICATION_INCHARGE,
  POWER_CONSUMER_APPLICATION_OPERATOR,
  SEWAGE_CONSUMER_APPLICATION_INCHARGE,
  SEWAGE_CONSUMER_APPLICATION_OPERATOR,
  SOLID_WASTE_CONSUMER_APPLICATION_INCHARGE,
  SOLID_WASTE_CONSUMER_APPLICATION_OPERATOR,
  WATER_CONSUMER_APPLICATION_INCHARGE,
  WATER_CONSUMER_APPLICATION_OPERATOR,
  applicationTypes,
} from "common/constant";
import Modal from "./Modal";
import InputFieldText from "./InputFieldText";
import ConfirmationDialog from "./ConfirmationDialog";
import { getOptionValue } from "common/enums";
import Backdrop from "./Backdrop";

import { filterEmptyStrings, isAuthorizedUser } from "utils";
import { getUtilityWiseRoles } from "utils/getUtilityWiseRoles";
import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";

import {
  usePutApprovedApplicationDocumentMutation,
  usePutApprovedOtherUtilitiesApplicationDocumentMutation,
} from "apis/ApprovedApplicationDocument";
import { useLazyGetConsumerNumberByGroupId } from "apis/UtilityConsumer/utilityConsumer";

const applicationIdsToAddConsumerNumber = [
  applicationTypes["Permanent Connection"],
  applicationTypes["Temporary Connection"],
];

const allowedRoles = [
  POWER_CONSUMER_APPLICATION_INCHARGE,
  POWER_CONSUMER_APPLICATION_OPERATOR,
  DCS_CONSUMER_APPLICATION_OPERATOR,
  DCS_CONSUMER_APPLICATION_INCHARGE,
  WATER_CONSUMER_APPLICATION_OPERATOR,
  WATER_CONSUMER_APPLICATION_INCHARGE,
  SEWAGE_CONSUMER_APPLICATION_INCHARGE,
  SEWAGE_CONSUMER_APPLICATION_OPERATOR,
  SOLID_WASTE_CONSUMER_APPLICATION_INCHARGE,
  SOLID_WASTE_CONSUMER_APPLICATION_OPERATOR,
];

//show signed document in admin side in modal view
const ApplicationFormPreviewModal = ({
  url,
  applicationType,
  groupId,
  applicationId,
  applicationStatus,
  openApplicationForm,
  handleClose,
  isApplicationTempBypassEnabled = false,
  utilities = [],
  t,
}) => {
  const [
    getConsumerNumberByGroupId,
    {
      data: { data: { consumerNumber: currentConsumerNumber = "" } = {} } = {},
      isLoading: isConsumerNumberLoading,
    },
  ] = useLazyGetConsumerNumberByGroupId();
  //store total pages of pdf
  const [isAddConsumerNumberModalOpen, setIsAddConsumerNumberModalOpen] =
    useState(false);
  const [isApproveButtonClicked, setIsApproveButtonClicked] = useState(false);
  const [isRejectionCommentModalVisible, setIsRejectionCommentModalVisible] =
    useState(false);

  const { roles } = useSelector((state) => state.Login);

  //api for approve or reject document
  const [documentVerification, { isLoading }] =
    usePutApprovedApplicationDocumentMutation();
  const [approveOtherUtilityForm, { isLoading: isFormApproving }] =
    usePutApprovedOtherUtilitiesApplicationDocumentMutation();

  const isPowerApplication = useMemo(
    () => utilities.includes(getOptionValue("utilityOptions", "Power")),
    [utilities]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      consumerNumber: currentConsumerNumber,
    },
    validationSchema: Yup.object({
      consumerNumber: getConsumerNumberValidation(Yup.string()),
    }),
    onSubmit: ({ consumerNumber }) => {
      approveApplicationForm(consumerNumber);
    },
  });

  const approveApplicationForm = async (consumerNumber = "") => {
    try {
      const payload = {
        applicationId,
        isApproved: true,
        consumerNumber,
      };
      if (isPowerApplication) {
        await documentVerification(filterEmptyStrings(payload));
      } else {
        await approveOtherUtilityForm(filterEmptyStrings(payload));
      }

      handleClose();
    } catch (err) {}
  };

  const rejectBtnSubmitHandler = async (comment) => {
    try {
      const payload = {
        applicationId: applicationId,
        isApproved: false,
      };
      const query = { applicationId, comment };
      if (isPowerApplication) {
        await await documentVerification(payload, query);
      } else {
        await approveOtherUtilityForm(payload, query);
      }

      handleClose();
    } catch (err) {}
  };

  useEffect(() => {
    (async () => {
      try {
        if (groupId && isAuthorizedUser(roles, allowedRoles)) {
          await getConsumerNumberByGroupId({ groupId });
        }
      } catch (error) {}
    })();
  }, [groupId, roles]);

  if (isConsumerNumberLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      {isAddConsumerNumberModalOpen && (
        <Modal
          isOpen
          handleClose={() => {
            setIsAddConsumerNumberModalOpen(false);
            formik.resetForm();
          }}
          title="Add Consumer Number"
        >
          <div className="mt-3">
            <InputFieldText
              formik={formik}
              name="consumerNumber"
              disabled={!!currentConsumerNumber}
              label={t("Consumer Number") + "*"}
              placeholder={t("Enter consumer number")}
            />
            <Button
              onClick={formik.handleSubmit}
              variant="contained"
              className="d-flex ms-auto gap-2"
              disabled={isLoading || isFormApproving}
            >
              {t("Submit")}
              {(isLoading || isFormApproving) && <CircularProgress size={20} />}
            </Button>
          </div>
        </Modal>
      )}

      {isApproveButtonClicked && (
        <ConfirmationDialog
          message={
            isApplicationTempBypassEnabled
              ? "This application will bypass the Security Deposit process after approval and move directly to \"Process Complete\" status. Are you sure you want to approve application?"
              : "Are you sure you want to approve application?"
          }
          onCancel={() => setIsApproveButtonClicked(false)}
          onConfirm={() => approveApplicationForm()}
          isOpen={isApproveButtonClicked}
          title="Warning"
          variant="warning"
          isLoading={isLoading}
        />
      )}

      <CommentInputForReason
        handleClose={() => {
          setIsRejectionCommentModalVisible(false);
        }}
        handleSubmit={rejectBtnSubmitHandler}
        isOpen={isRejectionCommentModalVisible}
        isLoading={isLoading && isRejectionCommentModalVisible}
        title="Add Reason for Rejection"
        errorMessage="Please enter reason for rejection"
      />

      <Dialog
        open={openApplicationForm}
        onClose={handleClose}
        maxWidth="lg"
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        PaperProps={{
          sx: {
            width: "100%",
            height: "80vh",
          },
        }}
      >
        <DialogTitle
          className="mb-2 d-flex justify-content-between"
          bgcolor="transparent"
          component="div"
        >
          <h3>{t(`${applicationType} Form`)}</h3>
          <div className="justify-content-end">
            <IconButton
              className="ri-close-line ri-lg"
              aria-label="close"
              onClick={handleClose}
            />
          </div>
        </DialogTitle>
        <embed
          src={url}
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
          type="application/pdf"
        />

        {applicationStatus.toLowerCase() === "approved" &&
          !!utilities.length &&
          isAuthorizedUser(roles, [
            ...getUtilityWiseRoles([utilities[0]]).admin3Roles,
            ...getUtilityWiseRoles([utilities[0]]).admin4Roles,
          ]) && (
            <DialogActions style={{ paddingRight: "24px" }}>
              <Button
                onClick={() => setIsRejectionCommentModalVisible(true)}
                variant="contained"
                color="error"
                disabled={isLoading}
              >
                {t("Reject")}
              </Button>
              <Button
                onClick={() => {
                  if (
                    applicationIdsToAddConsumerNumber.includes(
                      applicationTypes[applicationType]
                    )
                  ) {
                    setIsAddConsumerNumberModalOpen(true);
                  } else {
                    setIsApproveButtonClicked(true);
                  }
                }}
                variant="contained"
                color="success"
                disabled={isLoading}
              >
                {t("Approve")}
              </Button>
            </DialogActions>
          )}
      </Dialog>
    </>
  );
};

ApplicationFormPreviewModal.propTypes = {
  url: PropTypes.string,
  applicationType: PropTypes.string,
  applicationId: PropTypes.number,
  applicationStatus: PropTypes.string,
  handleClose: PropTypes.func,
  openApplicationForm: PropTypes.bool,
  groupId: PropTypes.string,
  utilities: PropTypes.array,
  isApplicationTempBypassEnabled: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(ApplicationFormPreviewModal);
